import { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../../lib/api'
import { uploadDocument } from '../../lib/portal'

const ACCEPT = '.pdf,.docx,.xlsx,.txt,.xml'
const ALLOWED_LABEL = 'PDF, DOCX, XLSX, TXT, XML'
const MAX_BYTES = 25 * 1024 * 1024 // 25 MB — matches DocumentsController.MaxUploadBytes.

/** True when the selected file is a PDF (routes to PAdES); everything else routes to CAdES. */
function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

export default function UploadDocumentPage() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile]   = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [busy, setBusy]   = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pick = (f: File | null) => {
    if (f && f.size > MAX_BYTES) {
      setError(`Dosya çok büyük (${fmtSize(f.size)}). En fazla 25 MB.`)
      return
    }
    setFile(f); setError(null)
  }

  const submit = async () => {
    if (!file) { setError('Bir dosya seçin.'); return }
    setBusy(true); setError(null)
    try {
      const doc = await uploadDocument(file, title.trim() || undefined)
      navigate(`/documents/${doc.id}`)
    } catch (err) {
      // Surface the backend's Turkish error message (allowlist / content-mismatch) verbatim.
      setError(err instanceof ApiError ? err.message : 'Yükleme başarısız.')
      setBusy(false)
    }
  }

  return (
    <>
      <Helmet><title>Belge yükle — W.Sign</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <section className="container-page py-12 max-w-2xl">
        <h1 className="text-3xl font-semibold">Belge yükle ve imzala</h1>
        <p className="mt-2 text-[var(--color-fg-muted)]">
          Belgenizi yükleyin; bir sonraki adımda nitelikli e-imzanızla imzalayın. Biçime göre
          otomatik olarak <strong>PDF için PAdES</strong>, diğer belgeler için <strong>CAdES</strong>
          imzası üretilir — sunucuda TSA yapılandırılıysa <strong>uzun dönemli ve zaman damgalı</strong>.
        </p>

        <div className="card mt-6 space-y-4">
          <input
            ref={inputRef}
            type="file" accept={ACCEPT}
            onChange={e => pick(e.target.files?.[0] ?? null)}
            className="hidden"
          />

          {/* Drag & drop / click zone */}
          <div
            role="button" tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click() } }}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0] ?? null) }}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragOver
                ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]'
                : 'border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]'
            }`}
          >
            {file ? (
              <>
                <span className="text-sm font-medium break-all">{file.name}</span>
                <span className="mt-1 text-xs text-[var(--color-fg-muted)]">{fmtSize(file.size)}</span>
                <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--color-bg-soft)] px-3 py-1 text-xs font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]" />
                  {isPdf(file)
                    ? 'PDF → PAdES imza'
                    : 'PDF dışı → CAdES imza'}
                </span>
                <span className="mt-3 text-xs text-[var(--color-accent)] underline underline-offset-2">Başka dosya seç</span>
              </>
            ) : (
              <>
                <span className="text-sm font-medium">Dosyayı buraya sürükleyin veya seçmek için tıklayın</span>
                <span className="mt-1 text-xs text-[var(--color-fg-muted)]">{ALLOWED_LABEL} · en fazla 25 MB</span>
              </>
            )}
          </div>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Başlık (opsiyonel)</span>
            <input
              type="text" value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={file ? file.name : 'Belge başlığı'}
              className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
            />
          </label>

          <div className="flex items-center gap-3">
            <button onClick={submit} disabled={busy || !file} type="button" className="btn-primary">
              {busy ? 'Yükleniyor…' : 'Yükle ve devam et'}
            </button>
            <span className="text-xs text-[var(--color-fg-muted)]">
              Belge sunucuda kalır; imza sırasında yalnızca özet ve ham imza taşınır.
            </span>
          </div>

          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </section>
    </>
  )
}

function fmtSize(n: number): string {
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`
}
